/**
 * `isDisplayNameFallback` — detect the cascade-step-6 fallback shape.
 *
 * Issue 010 / Brief 37 chunk 1. The backend's
 * `_derive_display_name` cascade in
 * `backend/src/auth/middleware.py:242-285` falls through six steps to
 * pick a human-readable name from the Clerk JWT. Step 6 is the final
 * fallback `User <first 8 of clerk_id>` — the row is never anonymous
 * in the audit feed but the user-pill reads `User 3D9mZIe8` until
 * Layer A (founder F6 — JWT template + sign-up name capture) lands or
 * the user manually edits via `/settings/profile` (Brief 29 / issue
 * 001).
 *
 * The in-app first-run gate (`<RequireDisplayName/>`) detects this
 * exact fallback shape so a new account on first sign-in is bounced
 * to the profile page with a calm welcome hero before reaching
 * `/teams/...`. A real name (anything that doesn't match the cascade
 * step-6 pattern) bypasses the gate.
 *
 * Pattern: `User ` + 8 alphanumeric characters. Clerk IDs are
 * alphanumeric (`user_3D9mZIe8FH5UXLlgc8TsEUkmhRH` style) so the
 * sliced 8-char handle matches `[A-Za-z0-9]{8}`. We anchor with `^` /
 * `$` so a longer name like "User Smith with surname User abcdefgh"
 * never trips the heuristic — only the exact fallback shape returns
 * true.
 *
 * Edge cases:
 *   - `null` / `undefined` / "" → false (no fallback to detect; the
 *     gate falls through to the outlet so the user can still reach
 *     `/settings/profile` via the user-pill menu).
 *   - "User Smith" → false (real surname; not the 8-char slug shape).
 *   - "User 1234" → false (only 4 digits; <8 chars).
 *   - "User abc12345" → true (8 alphanumeric chars).
 *   - "user 3D9mZIe8" (lowercase u) → false (cascade emits "User "
 *     with capital U; matching the exact backend literal avoids
 *     false-positives on user-typed names that happen to start
 *     with "user ").
 */
const FALLBACK_PATTERN = /^User [A-Za-z0-9]{8}$/;

export function isDisplayNameFallback(displayName: string | null | undefined): boolean {
  if (!displayName) return false;
  return FALLBACK_PATTERN.test(displayName);
}
